/**
 * BFP-1151 — Sign-Up voter answers stored on the responder profile and used as
 * Target Audience filters. Distinct from `registeredPoliticalParty` (account form / voter file).
 */

export const REGISTERED_TO_VOTE_VALUES = ['Yes', 'No', 'Not Sure'] as const
export type RegisteredToVoteValue = (typeof REGISTERED_TO_VOTE_VALUES)[number]

export const CURRENT_VOTER_REGISTRATION_VALUES = [
  'Democrat',
  'Republican',
  'Independent',
  'Other',
] as const
export type CurrentVoterRegistrationValue = (typeof CURRENT_VOTER_REGISTRATION_VALUES)[number]

export type VoterProfileAttributeKey = 'registeredToVote' | 'currentVoterRegistration'

const REGISTERED_TO_VOTE_PROMPTS = new Set(['are you currently registered to vote?'])

const CURRENT_VOTER_REGISTRATION_PROMPTS = new Set([
  'what is your current voter registraion?',
  'what is your current voter registration?',
])

export function normalizeVoterPrompt(prompt: string): string {
  return prompt.trim().toLowerCase().replace(/\s+/g, ' ')
}

export function voterAttributeForPrompt(prompt: string): VoterProfileAttributeKey | null {
  const normalized = normalizeVoterPrompt(prompt)
  if (REGISTERED_TO_VOTE_PROMPTS.has(normalized)) return 'registeredToVote'
  if (CURRENT_VOTER_REGISTRATION_PROMPTS.has(normalized)) return 'currentVoterRegistration'
  return null
}

export function normalizeRegisteredToVote(label: string): RegisteredToVoteValue | null {
  const normalized = label.trim().toLowerCase()
  if (normalized === 'yes') return 'Yes'
  if (normalized === 'no') return 'No'
  if (normalized === 'not sure') return 'Not Sure'
  return null
}

export function normalizeCurrentVoterRegistration(
  label: string,
): CurrentVoterRegistrationValue | null {
  const normalized = label.trim().toLowerCase()
  if (normalized === 'democrat') return 'Democrat'
  if (normalized === 'republican') return 'Republican'
  if (normalized === 'independent') return 'Independent'
  if (normalized === 'other') return 'Other'
  return null
}

export interface VoterQuestionOption {
  id?: string
  label?: string
}

export interface VoterQuestionSnapshot {
  id?: string
  prompt?: string
  options?: VoterQuestionOption[]
}

export interface VoterAnswerSnapshot {
  questionId: string
  structuredValue?: unknown
}

export interface ExtractedVoterProfileAttributes {
  registeredToVote?: RegisteredToVoteValue
  currentVoterRegistration?: CurrentVoterRegistrationValue
}

function selectedOptionToken(structuredValue: unknown): string | null {
  if (typeof structuredValue === 'string' || typeof structuredValue === 'number') {
    const token = String(structuredValue).trim()
    return token || null
  }
  if (Array.isArray(structuredValue) && structuredValue.length > 0) {
    const first = structuredValue[0]
    if (typeof first === 'string' || typeof first === 'number') {
      const token = String(first).trim()
      return token || null
    }
  }
  return null
}

function labelForAnswer(question: VoterQuestionSnapshot, structuredValue: unknown): string | null {
  const selected = selectedOptionToken(structuredValue)
  if (!selected) return null
  for (const option of question.options ?? []) {
    if (option.id != null && String(option.id) === selected) {
      const label = option.label?.trim()
      return label || null
    }
  }
  return selected
}

function canonicalForAttribute(
  attribute: VoterProfileAttributeKey,
  label: string,
): RegisteredToVoteValue | CurrentVoterRegistrationValue | null {
  if (attribute === 'registeredToVote') return normalizeRegisteredToVote(label)
  return normalizeCurrentVoterRegistration(label)
}

/** Latest answer in array order wins when one response contains the question more than once. */
export function extractVoterProfileAttributes(input: {
  questions: VoterQuestionSnapshot[]
  answers: VoterAnswerSnapshot[]
}): ExtractedVoterProfileAttributes {
  const questionsById = new Map<string, VoterQuestionSnapshot>()
  for (const question of input.questions) {
    if (question.id == null) continue
    questionsById.set(String(question.id), question)
  }

  const extracted: ExtractedVoterProfileAttributes = {}
  for (const answer of input.answers) {
    const question = questionsById.get(String(answer.questionId))
    if (!question?.prompt) continue
    const attribute = voterAttributeForPrompt(question.prompt)
    if (!attribute) continue
    const label = labelForAnswer(question, answer.structuredValue)
    if (!label) continue
    const canonical = canonicalForAttribute(attribute, label)
    if (!canonical) continue
    if (attribute === 'registeredToVote') {
      extracted.registeredToVote = canonical as RegisteredToVoteValue
    } else {
      extracted.currentVoterRegistration = canonical as CurrentVoterRegistrationValue
    }
  }
  return extracted
}

export interface VoterResponseForLatest {
  providerId: string
  submittedAt: Date | string
  questions: VoterQuestionSnapshot[]
  answers: VoterAnswerSnapshot[]
}

function submittedTime(value: Date | string): number {
  const time = value instanceof Date ? value.getTime() : Date.parse(value)
  return Number.isNaN(time) ? 0 : time
}

type VoterProviderAttributeSlot = {
  registeredToVoteAt: number
  currentVoterRegistrationAt: number
} & ExtractedVoterProfileAttributes

function isIgnoredVoterProviderId(providerId: string): boolean {
  return (
    providerId.length === 0 ||
    providerId.startsWith('guest:') ||
    providerId.startsWith('pending:')
  )
}

function emptyVoterProviderSlot(): VoterProviderAttributeSlot {
  return { registeredToVoteAt: 0, currentVoterRegistrationAt: 0 }
}

function mergeExtractedIntoVoterSlot(
  slot: VoterProviderAttributeSlot,
  extracted: ExtractedVoterProfileAttributes,
  submittedAt: number,
): void {
  if (extracted.registeredToVote && submittedAt >= slot.registeredToVoteAt) {
    slot.registeredToVote = extracted.registeredToVote
    slot.registeredToVoteAt = submittedAt
  }
  if (extracted.currentVoterRegistration && submittedAt >= slot.currentVoterRegistrationAt) {
    slot.currentVoterRegistration = extracted.currentVoterRegistration
    slot.currentVoterRegistrationAt = submittedAt
  }
}

function voterSlotToAttributes(slot: VoterProviderAttributeSlot): ExtractedVoterProfileAttributes {
  const attributes: ExtractedVoterProfileAttributes = {}
  if (slot.registeredToVote) attributes.registeredToVote = slot.registeredToVote
  if (slot.currentVoterRegistration) {
    attributes.currentVoterRegistration = slot.currentVoterRegistration
  }
  return attributes
}

/**
 * Newest submitted response per provider wins for each attribute independently.
 * Guest and pending provider ids are ignored.
 */
export function pickLatestVoterProfileAttributes(
  responses: VoterResponseForLatest[],
): Map<string, ExtractedVoterProfileAttributes> {
  const best = new Map<string, VoterProviderAttributeSlot>()

  const ordered = [...responses].sort(
    (left, right) => submittedTime(left.submittedAt) - submittedTime(right.submittedAt),
  )

  for (const response of ordered) {
    const providerId = response.providerId.trim()
    if (isIgnoredVoterProviderId(providerId)) continue

    const extracted = extractVoterProfileAttributes({
      questions: response.questions,
      answers: response.answers,
    })
    if (!extracted.registeredToVote && !extracted.currentVoterRegistration) continue

    const slot = best.get(providerId) ?? emptyVoterProviderSlot()
    mergeExtractedIntoVoterSlot(slot, extracted, submittedTime(response.submittedAt))
    best.set(providerId, slot)
  }

  const result = new Map<string, ExtractedVoterProfileAttributes>()
  for (const [providerId, slot] of best) {
    result.set(providerId, voterSlotToAttributes(slot))
  }
  return result
}
